import api from "./axios";
import {
  CreateTicketRequest,
  Ticket,
  TicketCategory,
  TicketCategoryRequest,
  TicketAttachment,
  TicketComment,
  TicketListFilter,
  TicketQueue,
  TicketSlaCompliance,
  TicketSlaFilter,
  TicketState,
} from "../interfaces/Ticket";

const BASE = "/Ticket";

/** Lista chamados (fila / estado / cliente / "meus"), conforme o contrato do backend. */
export async function getTickets(filter: TicketListFilter): Promise<Ticket[]> {
  const { data } = await api.get(BASE, {
    params: {
      queueId: filter.queueId ?? undefined,
      state: filter.state ?? undefined,
      clientId: filter.clientId ?? undefined,
      mine: filter.mine ?? undefined,
      month: filter.month ?? undefined,
      assigneeEmployeeId: filter.assigneeEmployeeId ?? undefined,
      unassigned: filter.unassigned || undefined,
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function getTicket(id: number): Promise<Ticket> {
  const { data } = await api.get(`${BASE}/${id}`);
  return data;
}

/** Abre um chamado em nome de um cliente (uso interno — cliente abre pelo Portal, não aqui). */
export async function createTicket(req: CreateTicketRequest): Promise<Ticket> {
  const { data } = await api.post(BASE, req);
  return data;
}

/** Assume o chamado para um colaborador (self-assign via "Assumir" ou atribuição a outra pessoa). */
export async function assignTicket(id: number, employeeId: number): Promise<void> {
  await api.patch(`${BASE}/${id}/assign`, { employeeId });
}

/** Move o chamado para outra fila (ou remove da fila, com `null`). */
export async function moveTicketQueue(id: number, queueId: number | null): Promise<void> {
  await api.patch(`${BASE}/${id}/queue`, { queueId });
}

/** Muda o estado do chamado — usado tanto pelo seletor genérico quanto pelos atalhos fechar/reabrir. */
export async function changeTicketState(id: number, state: TicketState): Promise<void> {
  await api.patch(`${BASE}/${id}/state`, { state });
}

/**
 * Reclassifica (triagem). `null` LIMPA a categoria — "isto não se encaixa em nada do catálogo" é
 * resposta legítima. Recalcula o SLA no servidor; a fila não muda junto.
 */
export async function setTicketCategory(id: number, categoryId: number | null): Promise<void> {
  await api.patch(`${BASE}/${id}/category`, { categoryId });
}

/** Comentários do chamado — internos veem tudo (públicos + notas internas). */
export async function getTicketComments(id: number): Promise<TicketComment[]> {
  const { data } = await api.get(`${BASE}/${id}/comments`);
  return Array.isArray(data) ? data : [];
}

/** Monta o multipart do contrato (A8): `arquivos` repetido, um campo por arquivo. */
function formDeArquivos(arquivos: File[], campos: Record<string, string> = {}): FormData {
  const form = new FormData();
  Object.entries(campos).forEach(([k, v]) => form.append(k, v));
  arquivos.forEach((a) => form.append("arquivos", a));
  return form;
}

/** Mensagem (texto e/ou arquivos) numa chamada só: tudo grava ou nada grava. */
export async function sendTicketMessage(
  id: number,
  req: { body: string; isInternal: boolean; arquivos: File[] }
): Promise<TicketComment> {
  const { data } = await api.post(
    `${BASE}/${id}/mensagens`,
    formDeArquivos(req.arquivos, { body: req.body, isInternal: String(req.isInternal) })
  );
  return data;
}

/** Sobe arquivos soltos no chamado (sem mensagem) — usado logo após abrir o chamado. */
export async function uploadTicketAttachments(
  id: number,
  arquivos: File[],
  isInternal = false
): Promise<TicketAttachment[]> {
  const { data } = await api.post(
    `${BASE}/${id}/attachments`,
    formDeArquivos(arquivos, { isInternal: String(isInternal) })
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Quantos chamados estão abertos e sem responsável — a fila que ninguém assumiu.
 *
 * Devolve 0 em qualquer falha de propósito: isto alimenta um contador no menu, e um erro aqui não
 * pode derrubar a navegação nem encher o console de quem só queria trocar de tela.
 */
export async function getUnassignedTicketCount(): Promise<number> {
  try {
    const { data } = await api.get(`${BASE}/unassigned-count`);
    return typeof data?.count === "number" ? data.count : 0;
  } catch {
    return 0;
  }
}

/** Anexos do chamado (metadados). */
export async function getTicketAttachments(id: number): Promise<TicketAttachment[]> {
  const { data } = await api.get(`${BASE}/${id}/attachments`);
  return Array.isArray(data) ? data : [];
}

/**
 * Baixa o anexo.
 *
 * ⚠ Não dá para usar um `<a href>` simples: o endpoint exige o Bearer, e link de navegação não
 * carrega o interceptor do axios. Busca como blob e dispara o download por URL de objeto — que é
 * revogada em seguida, senão cada download deixa o arquivo inteiro preso na memória da aba.
 */
export async function downloadTicketAttachment(attachmentId: number, fileName: string): Promise<void> {
  const { data } = await api.get(`${BASE}/attachments/${attachmentId}`, { responseType: "blob" });

  const url = window.URL.createObjectURL(data as Blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Adiado: revogar na hora pode cancelar o download em alguns navegadores.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
}

/** Filas de atendimento — para os seletores de filtro e de "mover de fila". */
export async function getTicketQueues(): Promise<TicketQueue[]> {
  const { data } = await api.get("/TicketQueue");
  return Array.isArray(data) ? data : [];
}

// ── Catálogo de categorias ──────────────────────────────────────────────────
// Leitura no piso `tickets.view`; escrita em `tickets.admin`. Vem em árvore (raízes com
// `children`) — os selects encadeados precisam saber quem é filha de quem.

const CATEGORIES = "/TicketCategory";

export async function getTicketCategories(includeInactive = false): Promise<TicketCategory[]> {
  const { data } = await api.get(CATEGORIES, { params: { includeInactive: includeInactive || undefined } });
  return Array.isArray(data) ? data : [];
}

export async function createTicketCategory(req: TicketCategoryRequest): Promise<TicketCategory> {
  const { data } = await api.post(CATEGORIES, req);
  return data;
}

export async function updateTicketCategory(id: number, req: TicketCategoryRequest): Promise<TicketCategory> {
  const { data } = await api.put(`${CATEGORIES}/${id}`, req);
  return data;
}

/** Desativa (soft-delete). Desativar uma raiz desativa as filhas — o servidor cuida disso. */
export async function deactivateTicketCategory(id: number): Promise<void> {
  await api.delete(`${CATEGORIES}/${id}`);
}

/** Reativa. Falha com 400 quando a categoria principal está desativada — reative-a primeiro. */
export async function reactivateTicketCategory(id: number): Promise<void> {
  await api.patch(`${CATEGORIES}/${id}/reactivate`);
}

/**
 * Exclui o chamado (soft-delete, `tickets.admin`). O motivo é obrigatório e fica na auditoria.
 * 409 = chamado com horas lançadas (feche em vez de excluir); a tela mostra a mensagem do servidor.
 */
export async function deleteTicket(id: number, reason: string): Promise<void> {
  await api.delete(`${BASE}/${id}`, { data: { reason } });
}

/** Indicador de SLA (coorte: chamados abertos no mês). Sem `month` = todos os meses. */
export async function getTicketSlaCompliance(filter: TicketSlaFilter): Promise<TicketSlaCompliance> {
  const { data } = await api.get(`${BASE}/sla-compliance`, {
    params: {
      month: filter.month ?? undefined,
      assigneeEmployeeId: filter.assigneeEmployeeId ?? undefined,
      clientId: filter.clientId ?? undefined,
      queueId: filter.queueId ?? undefined,
      unassigned: filter.unassigned || undefined,
    },
  });
  return data;
}
